// src/components/Chat/ChatSidebar.js

import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faFolderPlus, faChevronLeft, faFolder, faFolderOpen, faEllipsisV, faPencilAlt, faTrash, faSpinner } from '@fortawesome/free-solid-svg-icons';

const ChatSidebar = ({
  visible,
  onToggle,
  projects,
  chats,
  currentChatId,
  projectHooks,
  chatHooks,
  hasNextPage,
  isFetchingMore,
  loadMoreChats,
}) => {
  const [expandedProjects, setExpandedProjects] = useState(new Set());

  const toggleProject = (projectId) => {
    setExpandedProjects(prev => {
      const newSet = new Set(prev);
      if (newSet.has(projectId)) {
        newSet.delete(projectId);
      } else {
        newSet.add(projectId);
      }
      return newSet;
    });
  };

  const handleChatDrop = (chatId, projectId) => {
    chatHooks.actions.moveChatToProject(chatId, projectId);
  };
  
  if (!visible) {
    return null; // Если сайдбар невидимый, не рендерим его вообще
  }

  return (
    <div className="chat-sidebar">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5 className="mb-0">Управление</h5>
        <button
          className="btn btn-sm btn-outline-secondary sidebar-toggle"
          onClick={onToggle}
          title="Скрыть панель"
        >
          <FontAwesomeIcon icon={faChevronLeft} />
        </button>
      </div>
      
      <div 
        className="sidebar-item new-chat-item"
        onClick={() => chatHooks.actions.createNewChat()}
        title="Создать новый чат"
      >
        <FontAwesomeIcon icon={faEdit} className="me-2" />
        Новый чат
      </div>
      
      <div 
        className="sidebar-item new-project-item"
        onClick={projectHooks.actions.openCreateModal}
        title="Создать новый проект"
      >
        <FontAwesomeIcon icon={faFolderPlus} className="me-2" />
        Новый проект
      </div>

      {/* Проекты */}
      <div className="projects-section mt-3">
        {projects.map(project => {
          const projectChats = chats.filter(chat => chat.project && chat.project._id === project._id);
          const isExpanded = expandedProjects.has(project._id);
          
          return (
            <div key={project._id} className="project-block">
              <div className="project-folder-container">
                <div 
                  className="project-folder"
                  onClick={() => toggleProject(project._id)}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.currentTarget.classList.remove('drag-over');
                    const chatId = e.dataTransfer.getData('chatId');
                    if (chatId) handleChatDrop(chatId, project._id);
                  }}
                  onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('drag-over'); }}
                  onDragLeave={(e) => { e.preventDefault(); e.currentTarget.classList.remove('drag-over'); }}
                >
                  <FontAwesomeIcon 
                    icon={isExpanded ? faFolderOpen : faFolder} 
                    className="me-2"
                    style={{ color: project.color || '#007bff' }}
                  />
                  {projectHooks.editingProject === project._id ? (
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      defaultValue={project.name}
                      autoFocus
                      onBlur={(e) => projectHooks.actions.renameProject(project._id, e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') projectHooks.actions.renameProject(project._id, e.target.value);
                        if (e.key === 'Escape') projectHooks.actions.setEditingProject(null);
                      }}
                      onClick={(e) => e.stopPropagation()}
                    />
                  ) : (
                    <span className="project-name">{project.name}</span>
                  )}
                  <span className="chat-count">({projectChats.length})</span>
                </div>
                
                <div className="project-menu">
                  <button
                    className={`project-menu-btn ${projectHooks.projectMenuOpen === project._id ? 'active' : ''}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      projectHooks.actions.setProjectMenuOpen(projectHooks.projectMenuOpen === project._id ? null : project._id);
                    }}
                    title="Меню проекта"
                  >
                    <FontAwesomeIcon icon={faEllipsisV} />
                  </button>
                  
                  {projectHooks.projectMenuOpen === project._id && (
                    <div className="project-menu-dropdown">
                      <div className="project-menu-item edit" onClick={() => {
                        projectHooks.actions.setEditingProject(project._id);
                        projectHooks.actions.setProjectMenuOpen(null);
                      }}>
                        <FontAwesomeIcon icon={faPencilAlt} />
                        <span>Переименовать</span>
                      </div>
                      <div className="project-menu-item delete" onClick={() => projectHooks.actions.openDeleteModal(project._id)}>
                        <FontAwesomeIcon icon={faTrash} />
                        <span>Удалить</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              {isExpanded && (
                <div className="project-chats">
                  {projectChats.map(chat => (
                    <div
                      key={chat._id}
                      className={`project-chat-item ${currentChatId === chat._id ? 'active' : ''}`}
                      draggable={true}
                      onDragStart={(e) => {
                          e.dataTransfer.setData('internal/chat-drag', chat._id);
                          e.dataTransfer.setData('chatId', chat._id);
                      }}
                      onClick={() => chatHooks.actions.selectChat(chat._id)}
                    >
                      <span className="chat-title">{chat.title}</span>
                      <button
                        className="delete-chat-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          chatHooks.actions.openDeleteModal(chat._id);
                        }}
                        title="Удалить чат"
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </div>
                  ))}
                  {projectChats.length === 0 && <div className="empty-project"><span className="text-muted">Перетащите чаты сюда</span></div>}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Чаты без проекта */}
      <div className="no-project-section mt-3">
        <h6 className="section-title">Чаты</h6>
        <div 
            className="standalone-chats"
            onDrop={(e) => {
                e.preventDefault();
                e.currentTarget.classList.remove('drag-over');
                const chatId = e.dataTransfer.getData('chatId');
                if (chatId) handleChatDrop(chatId, null);
            }}
            onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('drag-over'); }}
            onDragLeave={(e) => { e.preventDefault(); e.currentTarget.classList.remove('drag-over'); }}
        >
            {chats.filter(chat => !chat.project).map(chat => (
            <div
                key={chat._id}
                className={`standalone-chat-item ${currentChatId === chat._id ? 'active' : ''}`}
                draggable={true}
                onDragStart={(e) => {
                    e.dataTransfer.setData('internal/chat-drag', chat._id);
                    e.dataTransfer.setData('chatId', chat._id);
                }}
                onClick={() => chatHooks.actions.selectChat(chat._id)}
            >
                <span className="chat-title">{chat.title}</span>
                <button
                className="delete-chat-btn"
                onClick={(e) => {
                    e.stopPropagation();
                    chatHooks.actions.openDeleteModal(chat._id);
                }}
                title="Удалить чат"
                >
                <FontAwesomeIcon icon={faTrash} />
                </button>
            </div>
            ))}
            {chats.filter(chat => !chat.project).length === 0 && (
                <div className="empty-chats"><span className="text-muted">Перетащите чаты сюда</span></div>
            )}

            {hasNextPage && (
              <div className="d-grid mt-2">
                <button 
                  className="btn btn-sm btn-outline-primary" 
                  onClick={loadMoreChats} 
                  disabled={isFetchingMore}
                >
                  {isFetchingMore ? (
                    <><FontAwesomeIcon icon={faSpinner} spin className="me-2" /> Загрузка...</>
                  ) : (
                    'Загрузить ещё'
                  )}
                </button>
              </div>
            )}
        </div>
      </div>
    </div>
  );
};

export default ChatSidebar;